import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../components/ui/Toast";
import {
  Button,
  Input,
  Textarea,
  Select,
  Card,
  FileUpload,
} from "../components/ui";

// Opzioni per select
const STATO_OPTIONS = [
  { value: 100000000, label: "Attivo" },
  { value: 100000001, label: "In scadenza" },
  { value: 100000002, label: "Scaduto" },
  { value: 100000003, label: "Annullato" },
];

const TIPO_IMPORTO_OPTIONS = [
  { value: 100000000, label: "Fisso annuale" },
  { value: 100000001, label: "A consumo" },
  { value: 100000002, label: "Misto" },
];

const MODALITA_RINNOVO_OPTIONS = [
  { value: 100000000, label: "Tacito rinnovo" },
  { value: 100000001, label: "Rinnovo esplicito" },
  { value: 100000002, label: "Nessun rinnovo" },
];

const BASE_PAGAMENTO_OPTIONS = [
  { value: 100000000, label: "Data fattura" },
  { value: 100000001, label: "Fine mese" },
  { value: 100000002, label: "Fine mese successivo" },
];

function NuovoContrattoPage() {
  const { apiRequest, canPerformAction } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Dati per select
  const [fornitori, setFornitori] = useState([]);
  const [rsaList, setRsaList] = useState([]);
  const [tipologieFornitura, setTipologieFornitura] = useState([]);

  // Form data
  const [formData, setFormData] = useState({
    nome: "",
    codice: "",
    fornitoreId: "",
    rsaId: "",
    tipologiaFornituraId: "",
    dataDecorrenza: "",
    dataFine: "",
    stato: 100000000, // Attivo di default
    importoAnnuo: "",
    importoMassimo: "",
    tipoImporto: "",
    giorniPagamento: "",
    basePagamento: "",
    modalitaRinnovo: "",
    giorniPreavviso: "",
    accordoQuadro: false,
    esclusiva: false,
    nomeReferente: "",
    emailReferente: "",
    telefonoReferente: "",
    orariPreferenziali: "",
    clausoleParticolari: "",
    noteSla: "",
    note: "",
  });

  const [documentiContratto, setDocumentiContratto] = useState([]);

  // Check permissions
  // Era `isAdmin || isRspp`: la scrittura contratti non e' piu' legata al ruolo RSPP ma
  // all'azione amministrabile da /admin/permessi-ruoli (backend: POST/PUT /api/contratti).
  const canCreate = canPerformAction("contratti.gestisci");

  useEffect(() => {
    if (!canCreate) {
      showToast("Non hai i permessi per creare contratti", "error");
      navigate("/contratti");
      return;
    }

    const fetchData = async () => {
      try {
        const [fornitoriRes, rsaRes, tipologieRes] = await Promise.all([
          apiRequest("/fornitori"),
          apiRequest("/rsa"),
          apiRequest("/tipologie-fornitura"),
        ]);

        setFornitori(fornitoriRes.value || []);
        setRsaList(rsaRes.value || []);
        setTipologieFornitura(tipologieRes.value || []);
      } catch (error) {
        console.error("Error fetching data:", error);
        showToast("Errore nel caricamento dati", "error");
      }
    };

    fetchData();
  }, [apiRequest, canCreate, navigate, showToast]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    // Clear error on change
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.nome.trim()) {
      newErrors.nome = "Il nome del contratto e obbligatorio";
    }
    if (!formData.fornitoreId) {
      newErrors.fornitoreId = "Seleziona un fornitore";
    }
    if (!formData.dataDecorrenza) {
      newErrors.dataDecorrenza = "La data di decorrenza e obbligatoria";
    }
    if (!formData.dataFine) {
      newErrors.dataFine = "La data di fine e obbligatoria";
    }
    if (
      formData.dataDecorrenza &&
      formData.dataFine &&
      formData.dataDecorrenza > formData.dataFine
    ) {
      newErrors.dataFine =
        "La data di fine deve essere successiva alla decorrenza";
    }
    if (
      formData.emailReferente &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.emailReferente)
    ) {
      newErrors.emailReferente = "Inserisci un indirizzo email valido";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      showToast("Correggi gli errori nel form", "error");
      return;
    }

    setLoading(true);

    try {
      // Prepara i dati
      const payload = {
        nome: formData.nome.trim(),
        codice: formData.codice.trim() || null,
        fornitoreId: formData.fornitoreId,
        rsaId: formData.rsaId || null,
        tipologiaFornituraId: formData.tipologiaFornituraId || null,
        dataDecorrenza: formData.dataDecorrenza,
        dataFine: formData.dataFine,
        stato: formData.stato,
        importoAnnuo: formData.importoAnnuo
          ? parseFloat(formData.importoAnnuo)
          : null,
        importoMassimo: formData.importoMassimo
          ? parseFloat(formData.importoMassimo)
          : null,
        tipoImporto: formData.tipoImporto || null,
        giorniPagamento: formData.giorniPagamento
          ? parseInt(formData.giorniPagamento)
          : null,
        basePagamento: formData.basePagamento || null,
        modalitaRinnovo: formData.modalitaRinnovo || null,
        giorniPreavviso: formData.giorniPreavviso
          ? parseInt(formData.giorniPreavviso)
          : null,
        accordoQuadro: formData.accordoQuadro,
        esclusiva: formData.esclusiva,
        nomeReferente: formData.nomeReferente.trim() || null,
        emailReferente: formData.emailReferente.trim() || null,
        telefonoReferente: formData.telefonoReferente.trim() || null,
        orariPreferenziali: formData.orariPreferenziali.trim() || null,
        clausoleParticolari: formData.clausoleParticolari.trim() || null,
        noteSla: formData.noteSla.trim() || null,
        note: formData.note.trim() || null,
      };

      // Crea il contratto
      const result = await apiRequest("/contratti", {
        method: "POST",
        body: JSON.stringify(payload),
      });

      // Upload documenti contratto se presenti
      if (documentiContratto.length > 0 && result.id) {
        let uploadErrors = 0;
        for (const file of documentiContratto) {
          try {
            const formDataUpload = new FormData();
            formDataUpload.append("file", file);
            formDataUpload.append("nome", file.name.replace(/\.[^/.]+$/, ""));
            formDataUpload.append("tipoDocumento", "100000000");
            formDataUpload.append(
              "rsaId",
              formData.rsaId || rsaList[0]?.id || "",
            );
            formDataUpload.append("contrattoId", result.id);
            formDataUpload.append("visibileFornitore", "true");
            if (formData.fornitoreId) {
              formDataUpload.append("fornitoreId", formData.fornitoreId);
            }
            if (formData.dataDecorrenza) {
              formDataUpload.append("dataDocumento", formData.dataDecorrenza);
            }

            await apiRequest("/documenti/upload", {
              method: "POST",
              body: formDataUpload,
              headers: {},
            });
          } catch (uploadError) {
            console.error("Error uploading document:", uploadError);
            uploadErrors++;
          }
        }
        if (uploadErrors > 0) {
          showToast(
            `Contratto creato. ${uploadErrors} file non caricati correttamente.`,
            "warning",
          );
          navigate("/contratti");
          return;
        }
      }

      showToast("Contratto creato con successo", "success");
      navigate("/contratti");
    } catch (error) {
      console.error("Error creating contratto:", error);
      showToast(
        error.message || "Errore nella creazione del contratto",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  // Genera codice automatico
  const generateCodice = () => {
    const fornitore = fornitori.find((f) => f.id === formData.fornitoreId);
    if (fornitore && formData.dataDecorrenza) {
      const year = new Date(formData.dataDecorrenza).getFullYear();
      const prefix = fornitore.nome.substring(0, 3).toUpperCase();
      const random = Math.floor(Math.random() * 1000)
        .toString()
        .padStart(3, "0");
      setFormData((prev) => ({
        ...prev,
        codice: `${prefix}-${year}-${random}`,
      }));
    }
  };

  if (!canCreate) {
    return null;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Nuovo Contratto</h1>
          <p className="text-gray-600">
            Crea un nuovo contratto con un fornitore
          </p>
        </div>
        <Button variant="outline" onClick={() => navigate("/contratti")}>
          Annulla
        </Button>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Informazioni Base */}
        <Card className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Informazioni Base
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Nome Contratto *"
              name="nome"
              value={formData.nome}
              onChange={handleChange}
              error={errors.nome}
              placeholder="es. Manutenzione Ascensori 2025"
            />

            <div className="flex gap-2">
              <Input
                label="Codice Contratto"
                name="codice"
                value={formData.codice}
                onChange={handleChange}
                placeholder="es. ASC-2025-001"
                className="flex-1"
              />
              <div className="flex items-end">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={generateCodice}
                  disabled={!formData.fornitoreId || !formData.dataDecorrenza}
                >
                  Auto
                </Button>
              </div>
            </div>

            <Select
              label="Fornitore *"
              name="fornitoreId"
              value={formData.fornitoreId}
              onChange={handleChange}
              error={errors.fornitoreId}
              options={[
                { value: "", label: "Seleziona fornitore..." },
                ...fornitori.map((f) => ({ value: f.id, label: f.nome })),
              ]}
            />

            <Select
              label="RSA"
              name="rsaId"
              value={formData.rsaId}
              onChange={handleChange}
              helper="Lascia vuoto per contratto generale (tutte le RSA)"
              options={[
                { value: "", label: "Tutte le RSA (generale)" },
                ...rsaList.map((r) => ({ value: r.id, label: r.nome })),
              ]}
            />

            <Select
              label="Tipologia Fornitura"
              name="tipologiaFornituraId"
              value={formData.tipologiaFornituraId}
              onChange={handleChange}
              options={[
                { value: "", label: "Seleziona tipologia..." },
                ...tipologieFornitura.map((t) => ({
                  value: t.id,
                  label: t.nome,
                })),
              ]}
            />

            <Select
              label="Stato"
              name="stato"
              value={formData.stato}
              onChange={handleChange}
              options={STATO_OPTIONS}
            />
          </div>
        </Card>

        {/* Date e Durata */}
        <Card className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Date e Durata
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              type="date"
              label="Data Decorrenza *"
              name="dataDecorrenza"
              value={formData.dataDecorrenza}
              onChange={handleChange}
              error={errors.dataDecorrenza}
            />

            <Input
              type="date"
              label="Data Fine *"
              name="dataFine"
              value={formData.dataFine}
              onChange={handleChange}
              error={errors.dataFine}
            />

            <Select
              label="Modalita Rinnovo"
              name="modalitaRinnovo"
              value={formData.modalitaRinnovo}
              onChange={handleChange}
              options={[
                { value: "", label: "Seleziona..." },
                ...MODALITA_RINNOVO_OPTIONS,
              ]}
            />

            <Input
              type="number"
              label="Giorni Preavviso"
              name="giorniPreavviso"
              value={formData.giorniPreavviso}
              onChange={handleChange}
              min="0"
              helper="Giorni di preavviso per disdetta"
            />
          </div>
        </Card>

        {/* Importi e Pagamenti */}
        <Card className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Importi e Pagamenti
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              type="number"
              label="Importo Annuo (EUR)"
              name="importoAnnuo"
              value={formData.importoAnnuo}
              onChange={handleChange}
              min="0"
              step="0.01"
              placeholder="0.00"
            />

            <Input
              type="number"
              label="Importo Massimo (EUR)"
              name="importoMassimo"
              value={formData.importoMassimo}
              onChange={handleChange}
              min="0"
              step="0.01"
              placeholder="0.00"
              helper="Importo massimo contrattuale"
            />

            <Select
              label="Tipo Importo"
              name="tipoImporto"
              value={formData.tipoImporto}
              onChange={handleChange}
              options={[
                { value: "", label: "Seleziona..." },
                ...TIPO_IMPORTO_OPTIONS,
              ]}
            />

            <Input
              type="number"
              label="Giorni Pagamento"
              name="giorniPagamento"
              value={formData.giorniPagamento}
              onChange={handleChange}
              min="0"
              placeholder="30"
              helper="es. 30, 60, 90 gg"
            />

            <Select
              label="Base Pagamento"
              name="basePagamento"
              value={formData.basePagamento}
              onChange={handleChange}
              options={[
                { value: "", label: "Seleziona..." },
                ...BASE_PAGAMENTO_OPTIONS,
              ]}
            />
          </div>

          {/* Flags */}
          <div className="flex gap-6 mt-4 pt-4 border-t">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                name="accordoQuadro"
                checked={formData.accordoQuadro}
                onChange={handleChange}
                className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700">Accordo Quadro</span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                name="esclusiva"
                checked={formData.esclusiva}
                onChange={handleChange}
                className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700">Esclusiva</span>
            </label>
          </div>
        </Card>

        {/* Referente */}
        <Card className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Referente Contratto
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              label="Nome Referente"
              name="nomeReferente"
              value={formData.nomeReferente}
              onChange={handleChange}
              placeholder="Nome e cognome"
            />

            <Input
              type="email"
              label="Email Referente"
              name="emailReferente"
              value={formData.emailReferente}
              onChange={handleChange}
              error={errors.emailReferente}
              placeholder="email@esempio.it"
            />

            <Input
              type="tel"
              label="Telefono Referente"
              name="telefonoReferente"
              value={formData.telefonoReferente}
              onChange={handleChange}
              placeholder="+39..."
            />

            <Input
              label="Orari Preferenziali"
              name="orariPreferenziali"
              value={formData.orariPreferenziali}
              onChange={handleChange}
              placeholder="es. Lun-Ven 9:00-18:00"
            />
          </div>
        </Card>

        {/* Clausole e Note */}
        <Card className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Clausole e Note
          </h2>

          <div className="space-y-4">
            <Textarea
              label="Clausole Particolari"
              name="clausoleParticolari"
              value={formData.clausoleParticolari}
              onChange={handleChange}
              rows={3}
              placeholder="Eventuali clausole particolari del contratto..."
            />

            <Textarea
              label="Note SLA"
              name="noteSla"
              value={formData.noteSla}
              onChange={handleChange}
              rows={3}
              placeholder="Livelli di servizio concordati (SLA)..."
            />

            <Textarea
              label="Note Generali"
              name="note"
              value={formData.note}
              onChange={handleChange}
              rows={3}
              placeholder="Altre note..."
            />
          </div>
        </Card>

        {/* Documenti */}
        <Card className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Documenti Contratto
          </h2>

          <FileUpload
            accept=".pdf,.doc,.docx"
            maxSize={100}
            multiple
            onChange={(files) => setDocumentiContratto(files || [])}
            value={documentiContratto.length > 0 ? documentiContratto : null}
            helper="Carica uno o piu documenti del contratto (PDF, DOC, DOCX - max 100MB ciascuno)"
          />
        </Card>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/contratti")}
          >
            Annulla
          </Button>
          <Button type="submit" loading={loading}>
            Crea Contratto
          </Button>
        </div>
      </form>
    </div>
  );
}

export default NuovoContrattoPage;
